import { Button, CombinedButton } from '@timelyapp/react';
import DatePickerDropdown from 'components/datepicker/DatePickerDropdown';
import { Container } from 'components/ui-blocks/container';
import { formatWeekRange } from 'domains/ai/timesheet_proposals/utils/util.dateFormatting';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { AgentTableWorkspace } from 'routes/agentTracking/AgentTableWorkspace';
import AgentTrackingControls from 'routes/agentTracking/AgentTrackingControls';
import AgentTrackingSessions from 'routes/agentTracking/AgentTrackingSessions';
import {
  type AgentSessionFilters,
  countSessions,
  DEFAULT_SESSION_SORT,
  EMPTY_SESSION_FILTERS,
  filterAgentPeriods,
  formatDuration,
  formatEstimatedCost,
  formatTokens,
  type SessionSort,
  summarizePeriods,
} from 'routes/agentTracking/agentTracking.model';
import {
  type AgentSessionsFetchers,
  type AgentSessionsState,
  useAgentSessions,
} from 'routes/agentTracking/useAgentSessions';
import {
  ColleagueAgentSessionsChanges,
  useAgentSessionsChangedEvent,
  useLiveRefresh,
} from 'routes/agentTracking/useLiveAgentSessions';
import { useMarkProjectsUsed } from 'routes/agentTracking/useMarkProjectsUsed';
import { TITLE_TEMPLATE } from 'utils/routing';
import { getIsUserTeamLead } from '~redux/selectors/teams';
import { getIsUserAdmin, getUserId } from '~redux/selectors/user';
import type { ThunkDispatch } from '~types/redux';
import { requestOwnerProjects } from './AgentProjectPicker';
import { periodsInViewOrder } from './bulkProjectPredictions';
import styles from './ProfileAgents.module.scss';

export type AgentProfileScope = { kind: 'project' | 'user'; id: number; name: string };

/**
 * A user's or project's agent sessions. Another user's profile lists what Rails lets the
 * viewer see of that person's sessions, editable where it says so (TT-6346).
 */
export default function ProfileAgents({
  scope,
  fetchers,
}: {
  scope: AgentProfileScope;
  fetchers?: AgentSessionsFetchers;
}) {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState(EMPTY_SESSION_FILTERS);
  const [sort, setSort] = useState<SessionSort>(DEFAULT_SESSION_SORT);

  const parsed = DateTime.fromISO(params.get('date') || '');
  const date = parsed.isValid ? parsed : DateTime.local();
  const from = date.startOf('week').toISODate()!;
  const to = date.endOf('week').toISODate()!;
  const navigate = (next: DateTime) => setParams({ date: next.toISODate()! });
  // Owned here, above the keyed view, so Refresh and week changes keep the mutation queue and
  // publication bookkeeping: a fresh instance could show a just-published session as private.
  const markProjectsUsed = useMarkProjectsUsed();
  const viewerId = useSelector(getUserId);
  const colleagueId = scope.kind === 'user' && scope.id !== viewerId ? scope.id : undefined;
  const sessions = useAgentSessions(
    { from, to, userId: colleagueId },
    fetchers,
    false,
    colleagueId ? undefined : markProjectsUsed
  );
  const dispatch = useDispatch<ThunkDispatch>();
  useEffect(() => {
    if (colleagueId) void requestOwnerProjects(dispatch, colleagueId);
  }, [dispatch, colleagueId]);
  // The viewer's own changes arrive on their own channel; a colleague's from the workspace
  // channel (ColleagueAgentSessionsChanges below), which only admins and team leads hold.
  const onLiveChange = useLiveRefresh({
    from,
    to,
    periods: sessions.periods,
    refresh: sessions.refresh,
    enabled: sessions.complete && !sessions.error,
  });
  useAgentSessionsChangedEvent(
    useCallback(
      (message: unknown) => (colleagueId ? undefined : onLiveChange(message)),
      [colleagueId, onLiveChange]
    )
  );
  const isAdmin = useSelector(getIsUserAdmin);
  const isTeamLead = useSelector(getIsUserTeamLead);

  return (
    <Container className={styles.root} includeMinimumPadding>
      {colleagueId && (isAdmin || isTeamLead) && (
        <ColleagueAgentSessionsChanges userId={colleagueId} onChange={onLiveChange} />
      )}
      <Helmet title={`${scope.name} - Agents`} titleTemplate={TITLE_TEMPLATE} />
      <div className={styles.toolbar}>
        <div>
          <h2>Agents</h2>
          {scope.kind === 'project' && <p>Your agent sessions assigned to this project.</p>}
        </div>
        <div className={styles.controls}>
          <CombinedButton>
            <Button
              size="small"
              aria-label="Previous week"
              onClick={() => navigate(date.minus({ weeks: 1 }))}
            >
              <FontAwesome name="chevron-left" />
            </Button>
            <Button
              size="small"
              aria-label="Next week"
              onClick={() => navigate(date.plus({ weeks: 1 }))}
            >
              <FontAwesome name="chevron-right" />
            </Button>
            <DatePickerDropdown
              initialMode="week"
              startOfWeek={1}
              baseDate={date.toISODate()!}
              dateRange={{ from, to }}
              onDateChange={next => navigate(DateTime.fromISO(next))}
              size="small"
            />
          </CombinedButton>
          {!date.hasSame(DateTime.local(), 'week') && (
            <Button size="small" onClick={() => navigate(DateTime.local())}>
              This week
            </Button>
          )}
          <Button size="small" onClick={sessions.reload}>
            Refresh
          </Button>
        </div>
      </div>
      <ProfileAgentSessions
        key={`${scope.kind}:${scope.id}:${from}`}
        scope={scope}
        from={from}
        to={to}
        sessions={sessions}
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
      />
    </Container>
  );
}

function ProfileAgentSessions({
  scope,
  from,
  to,
  sessions,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
}: {
  filters: AgentSessionFilters;
  onFiltersChange: (filters: AgentSessionFilters) => void;
  sort: SessionSort;
  onSortChange: (sort: SessionSort) => void;
  scope: AgentProfileScope;
  from: string;
  to: string;
  sessions: AgentSessionsState;
}) {
  const {
    periods,
    loading,
    error,
    truncated,
    deleteSession,
    assignReview,
    setPublished,
    changeSession,
  } = sessions;
  const viewerId = useSelector(getUserId);
  // Publishing and deleting stay with the owner; on a project page that's the viewer.
  const ownView = scope.kind === 'project' || scope.id === viewerId;
  // Assignment belongs to a period: never include another project's part of a shared session.
  const assigned = useMemo(
    () =>
      periods.filter(period =>
        scope.kind === 'project' ? period.projectId === scope.id : period.userId === scope.id
      ),
    [periods, scope.kind, scope.id]
  );
  const filtered = useMemo(() => filterAgentPeriods(assigned, filters), [assigned, filters]);
  const viewPeriods = useMemo(
    () => periodsInViewOrder([{ key: 'all', title: '', periods: filtered }], sort),
    [filtered, sort]
  );
  const totals = summarizePeriods(filtered);
  const agentSheetUrl = `/agent-tracking?date=${from}&view=week`;
  return (
    <section
      aria-label={scope.kind === 'project' ? 'Project agent sessions' : 'User agent sessions'}
    >
      <div className={styles.sectionHeader}>
        <h3>{formatWeekRange(from, to)}</h3>
        <Link to={agentSheetUrl}>Open Agent Tracking</Link>
      </div>
      {loading ? (
        <p role="status">Loading agent sessions…</p>
      ) : error ? (
        <p role="alert">Could not load agent sessions. Try Refresh.</p>
      ) : (
        <>
          {truncated && (
            <p role="status">
              Only the most recent sessions were returned. The list and totals may be incomplete.
            </p>
          )}
          {assigned.length ? (
            <>
              <dl className={styles.metrics}>
                <div>
                  <dt>Sessions</dt>
                  <dd>{countSessions(filtered)}</dd>
                </div>
                <div>
                  <dt>Agent time</dt>
                  <dd>{formatDuration(totals.seconds)}</dd>
                </div>
                <div>
                  <dt>Tokens</dt>
                  <dd>{formatTokens(totals.tokens)}</dd>
                </div>
                <div>
                  <dt>Estimated cost</dt>
                  <dd>{formatEstimatedCost(filtered, totals.cost)}</dd>
                </div>
              </dl>
              <AgentTableWorkspace
                periods={filtered}
                predictionPeriods={viewPeriods}
                onAssign={assignReview}
                onDelete={ownView ? deleteSession : undefined}
                onPublish={ownView ? setPublished : undefined}
                changeSession={changeSession}
                controls={<AgentTrackingControls filters={filters} onChange={onFiltersChange} />}
              >
                {filtered.length ? (
                  <AgentTrackingSessions
                    periods={filtered}
                    sort={sort}
                    onSortChange={onSortChange}
                    projectName={scope.kind === 'project' ? scope.name : undefined}
                    showDates
                    totalLabel={scope.kind === 'project' ? 'Project total' : 'User total'}
                  />
                ) : (
                  <p role="status">No matching sessions. Try changing or clearing your filters.</p>
                )}
              </AgentTableWorkspace>
            </>
          ) : (
            <div className={styles.empty}>
              <h3>
                {scope.kind === 'project'
                  ? 'No assigned agent sessions this week'
                  : 'No agent sessions this week'}
              </h3>
              {scope.kind === 'project' && (
                <p>
                  Assign activity periods to this project in{' '}
                  <Link to={agentSheetUrl}>Agent Tracking</Link>, or choose another week.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
